import React, { useEffect, useState } from "react";
import { Modal, Button, Table, Form, Row, Col } from "react-bootstrap";
import axios from "axios";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPen,
  faTrash,
  faClockRotateLeft,
} from "@fortawesome/free-solid-svg-icons";

export default function AssetTypeHistoryModal({
  show,
  onHide,
  assetType,
  onRefresh,
}) {
  const [entries, setEntries] = useState([]);

  // Update modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);

  // Loading
  const [loading, setLoading] = useState(false);

  // Update form
  const [formData, setFormData] = useState({
    assetname: "",
    cost: "",
    quantity: "",
    purchasedDate: "",
    purchasedFrom: "",
    company: "",
    description: "",
  });

  // =====================================================
  // SET PURCHASE HISTORY
  // =====================================================

  useEffect(() => {
    if (assetType) {
      setEntries(assetType.entries || []);
    }
  }, [assetType]);

  // =====================================================
  // FORMAT DATE
  // =====================================================

  const formatDate = (date) => {
    if (!date) return "-";

    const formattedDate = new Date(date);

    if (isNaN(formattedDate.getTime())) {
      return "-";
    }

    return formattedDate.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // =====================================================
  // OPEN EDIT MODAL
  // =====================================================

  const handleEdit = (entry) => {
    setSelectedEntry(entry);

    setFormData({
      assetname: entry.assetname || assetType?.assetname || "",
      cost: entry.cost ?? "",
      quantity: entry.quantity ?? "",
      purchasedDate: entry.purchasedDate
        ? new Date(entry.purchasedDate).toISOString().split("T")[0]
        : "",
      purchasedFrom: entry.purchasedFrom || "",
      company: entry.company || "",
      description: entry.description || "",
    });

    setShowEditModal(true);
  };

  // =====================================================
  // HANDLE INPUT CHANGE
  // =====================================================

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // =====================================================
  // CLOSE UPDATE MODAL
  // =====================================================

  const closeEditModal = () => {
    if (loading) return;

    setShowEditModal(false);
    setSelectedEntry(null);
  };

  // =====================================================
  // UPDATE ASSET PURCHASE
  // =====================================================

  const handleUpdate = async () => {
    const token = localStorage.getItem("token");

    if (!selectedEntry?._id) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Purchase ID is missing.",
      });

      return;
    }

    // -------------------------------
    // Validation
    // -------------------------------

    if (!formData.assetname.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Asset name is required.",
      });

      return;
    }

    if (formData.cost === "" || Number(formData.cost) < 0) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please enter a valid cost.",
      });

      return;
    }

    if (formData.quantity === "" || Number(formData.quantity) <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Quantity must be greater than 0.",
      });

      return;
    }

    if (!formData.purchasedDate) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Purchase date is required.",
      });

      return;
    }

    // =================================================
    // SWEETALERT CONFIRMATION
    // =================================================

    const result = await Swal.fire({
      icon: "warning",
      title: "Update Asset Purchase?",
      text: "Are you sure you want to update this purchase record?",
      showCancelButton: true,
      confirmButtonText: "Yes, Update",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#6c757d",

      // VERY IMPORTANT
      // Keep SweetAlert above Bootstrap modal
      customClass: {
        container: "asset-purchase-swal-container",
      },
    });

    if (!result.isConfirmed) {
      return;
    }

    // =================================================
    // API UPDATE
    // =================================================

    try {
      setLoading(true);

      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/api/v1/asset-type/${selectedEntry._id}`,
        {
          assetname: formData.assetname,
          cost: Number(formData.cost),
          quantity: Number(formData.quantity),
          purchasedDate: formData.purchasedDate,
          purchasedFrom: formData.purchasedFrom,
          company: formData.company,
          description: formData.description,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data.success) {
        // Update local entry
        setEntries((prev) =>
          prev.map((item) =>
            item._id === selectedEntry._id
              ? {
                  ...item,
                  ...formData,
                  cost: Number(formData.cost),
                  quantity: Number(formData.quantity),
                }
              : item,
          ),
        );

        // Close update modal
        setShowEditModal(false);
        setSelectedEntry(null);

        // Refresh parent table
        if (onRefresh) {
          onRefresh();
        }

        // Success popup
        await Swal.fire({
          icon: "success",
          title: "Updated Successfully",
          text: "Asset purchase has been updated.",
          timer: 1500,
          showConfirmButton: false,

          customClass: {
            container: "asset-purchase-swal-container",
          },
        });
      }
    } catch (error) {
      console.error("Update Asset Purchase Error:", error);

      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text:
          error?.response?.data?.message || "Unable to update asset purchase.",

        customClass: {
          container: "asset-purchase-swal-container",
        },
      });
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // DELETE ASSET PURCHASE
  // =====================================================

  const handleDelete = async (entry) => {
    if (!entry?._id) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Purchase ID is missing.",
        customClass: {
          container: "asset-purchase-swal-container",
        },
      });

      return;
    }

    const result = await Swal.fire({
      icon: "warning",
      title: "Delete Asset Purchase?",
      html: `
        <div>
          Are you sure you want to delete this purchase record?
          <br />
          <strong>
            ${entry.assetname || assetType?.assetname || ""}
          </strong>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",

      customClass: {
        container: "asset-purchase-swal-container",
      },
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      const token = localStorage.getItem("token");

      const response = await axios.delete(
        `${process.env.REACT_APP_BASE_URL}/api/v1/asset-type/${entry._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data.success) {
        setEntries((prev) => prev.filter((item) => item._id !== entry._id));

        if (onRefresh) {
          onRefresh();
        }

        Swal.fire({
          icon: "success",
          title: "Deleted Successfully",
          text: "Asset purchase has been deleted.",
          timer: 1500,
          showConfirmButton: false,

          customClass: {
            container: "asset-purchase-swal-container",
          },
        });
      }
    } catch (error) {
      console.error("Delete Asset Purchase Error:", error);

      Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text:
          error?.response?.data?.message || "Unable to delete asset purchase.",

        customClass: {
          container: "asset-purchase-swal-container",
        },
      });
    }
  };

  if (!assetType) {
    return null;
  }

  return (
    <>
      {/* =====================================================
          PURCHASE HISTORY MODAL
      ===================================================== */}

      <Modal
        show={show}
        onHide={onHide}
        size="xl"
        centered
        backdrop="static"
        className="asset-purchase-history-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faClockRotateLeft} className="me-2" />

            {assetType.assetname || assetType._id}
            {" - Purchase History"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* =================================================
              SUMMARY
          ================================================= */}

          <Row className="mb-4">
            <Col md={3}>
              <div className="border rounded p-3 text-center">
                <div className="text-muted">Total Amount</div>

                <h5 className="mb-0">
                  ₹ {Number(assetType.totalCost || 0).toLocaleString("en-IN")}
                </h5>
              </div>
            </Col>

            <Col md={3}>
              <div className="border rounded p-3 text-center">
                <div className="text-muted">Total Quantity</div>

                <h5 className="mb-0">{assetType.totalQuantity || 0}</h5>
              </div>
            </Col>

            <Col md={3}>
              <div className="border rounded p-3 text-center">
                <div className="text-muted">Used</div>

                <h5 className="mb-0">{assetType.totalUsed || 0}</h5>
              </div>
            </Col>

            <Col md={3}>
              <div className="border rounded p-3 text-center">
                <div className="text-muted">Available</div>

                <h5 className="mb-0">{assetType.totalAvailable || 0}</h5>
              </div>
            </Col>
          </Row>

          {/* =================================================
              PURCHASE HISTORY TABLE
          ================================================= */}

          <div className="table-responsive">
            <Table bordered hover responsive>
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Asset Name</th>
                  <th>Cost</th>
                  <th>Quantity</th>
                  <th>Purchase Date</th>
                  <th>Purchased From</th>
                  <th>Company</th>
                  <th>Description</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {entries.length > 0 ? (
                  entries.map((entry, index) => (
                    <tr key={entry._id || index}>
                      <td>{index + 1}</td>

                      <td>{entry.assetname || assetType.assetname || "-"}</td>

                      <td>
                        ₹ {Number(entry.cost || 0).toLocaleString("en-IN")}
                      </td>

                      <td>{entry.quantity || 0}</td>

                      <td>{formatDate(entry.purchasedDate)}</td>

                      <td>{entry.purchasedFrom || "-"}</td>

                      <td>{entry.company || "-"}</td>

                      <td>{entry.description || "-"}</td>

                      <td>
                        <div className="d-flex gap-2">
                          {/* EDIT */}
                          <Button
                            variant="outline-primary"
                            size="sm"
                            title="Edit Purchase"
                            onClick={() => handleEdit(entry)}
                          >
                            <FontAwesomeIcon icon={faPen} />
                          </Button>

                          {/* DELETE */}
                          <Button
                            variant="outline-danger"
                            size="sm"
                            title="Delete Purchase"
                            onClick={() => handleDelete(entry)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center py-4">
                      No purchase history found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* =====================================================
          UPDATE ASSET PURCHASE MODAL
      ===================================================== */}

      <Modal
        show={showEditModal}
        onHide={closeEditModal}
        centered
        backdrop="static"
        /*
         * IMPORTANT:
         * Dedicated backdrop class.
         */
        backdropClassName="update-asset-purchase-backdrop"
        className="update-asset-purchase-modal"
      >
        <Modal.Header closeButton={!loading}>
          <Modal.Title>Update Asset Purchase</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Row>
              {/* Asset Name */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Asset Name <span className="text-danger">*</span>
                  </Form.Label>

                  <Form.Control
                    type="text"
                    name="assetname"
                    value={formData.assetname}
                    onChange={handleChange}
                    placeholder="Enter asset name"
                  />
                </Form.Group>
              </Col>

              {/* Cost */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Cost <span className="text-danger">*</span>
                  </Form.Label>

                  <Form.Control
                    type="number"
                    name="cost"
                    value={formData.cost}
                    onChange={handleChange}
                    placeholder="Enter cost"
                    min="0"
                  />
                </Form.Group>
              </Col>

              {/* Quantity */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Quantity <span className="text-danger">*</span>
                  </Form.Label>

                  <Form.Control
                    type="number"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleChange}
                    placeholder="Enter quantity"
                    min="1"
                  />
                </Form.Group>
              </Col>

              {/* Purchase Date */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Purchase Date <span className="text-danger">*</span>
                  </Form.Label>

                  <Form.Control
                    type="date"
                    name="purchasedDate"
                    value={formData.purchasedDate}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>

              {/* Purchased From */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Purchased From</Form.Label>

                  <Form.Control
                    type="text"
                    name="purchasedFrom"
                    value={formData.purchasedFrom}
                    onChange={handleChange}
                    placeholder="Enter purchased from"
                  />
                </Form.Group>
              </Col>

              {/* Company */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Company</Form.Label>

                  <Form.Control
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Enter company"
                  />
                </Form.Group>
              </Col>

              {/* Description */}
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>

                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Enter description"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeEditModal}
            disabled={loading}
          >
            Cancel
          </Button>

          <Button variant="primary" onClick={handleUpdate} disabled={loading}>
            {loading ? "Updating..." : "Update"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* =====================================================
          ALL REQUIRED CSS
      ===================================================== */}

      <style>
        {`
          /* =================================================
             1. PURCHASE HISTORY MODAL
          ================================================= */

          .asset-purchase-history-modal {
            z-index: 1050 !important;
          }

          .asset-purchase-history-modal
            .modal-dialog {
            z-index: 1051 !important;
          }


          /* =================================================
             2. UPDATE MODAL BACKDROP

             This sits above Purchase History.
          ================================================= */

          .modal-backdrop.update-asset-purchase-backdrop {
            z-index: 1060 !important;

            opacity: 1 !important;

            background-color: rgba(
              0,
              0,
              0,
              0.35
            ) !important;

            backdrop-filter: blur(8px) !important;

            -webkit-backdrop-filter: blur(8px) !important;
          }


          /* =================================================
             3. UPDATE ASSET PURCHASE MODAL

             This sits above its backdrop.
          ================================================= */

          .update-asset-purchase-modal {
            z-index: 1070 !important;
          }

          .update-asset-purchase-modal
            .modal-dialog {
            z-index: 1071 !important;
          }

          .update-asset-purchase-modal
            .modal-content {
            position: relative;

            z-index: 1072 !important;

            border-radius: 8px;

            filter: none !important;

            backdrop-filter: none !important;

            -webkit-backdrop-filter: none !important;
          }


          /* =================================================
             4. SWEETALERT2

             IMPORTANT:
             SweetAlert must appear ABOVE the
             Update Asset Purchase modal.
          ================================================= */

          .swal2-container {
            z-index: 20000 !important;
          }

          .asset-purchase-swal-container {
            z-index: 20000 !important;
          }

          .swal2-popup {
            z-index: 20001 !important;
          }


          /* =================================================
             5. SWEETALERT BACKDROP
          ================================================= */

          .swal2-container.swal2-backdrop-show {
            z-index: 20000 !important;
          }


          /* =================================================
             6. UPDATE MODAL FORM
          ================================================= */

          .update-asset-purchase-modal
            .form-control {
            position: relative;
            z-index: 1073;
          }


          /* =================================================
             7. BUTTONS
          ================================================= */

          .update-asset-purchase-modal
            .modal-footer
            button {
            min-width: 90px;
          }
        `}
      </style>
    </>
  );
}
